import { Alert, Box, Stack } from "@mui/material";
import { isNil } from "lodash-es";

import InlineUser from "@/common/components/Entity/User/InlineUser";
import { TypeHelper } from "@/common/helpers/type";
import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import { ApiEnumName } from "@/common/services/enum";
import {
  ApplyStatus,
  DamageCorrectionStatus,
  DamageCostEvaluationDamageCorrectionsDto,
  DamageCostEvaluationDto,
  DamageCostEvaluationStage,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../../AppAlerts/NoDataAlert";
import Datetime from "../../../Datetime/Datetime";
import InlineApiEnumValue from "../../../Enum/InlineApiEnumValue";
import FieldValue from "../../../Form/Display/FieldValue";
import InfoTile from "../../../Form/Display/InfoTile";
import AppTypography from "../../../Text/AppTypography";
import TenantLink from "../../Tenant/TenantLink";
import VehicleDamageLink from "../../VehicleDamage/VehicleDamageLink";
import { VisualModelViewerOfDamageCostEvaluation } from "../../VehicleVisualModel/Viewer/v2/Entity/VisualModelViewerOfDamageCostEvaluation";
import { DamageCostEvaluationDamageCorrectionsOverviewDisplay } from "./DamageCostEvaluationDamageCorrectionsOverviewDisplay";

const defaultDisplayProps = {
  alertsSection: true,
  overviewSection: true,
  damagesOnVisualModelsSection: true,
  appliedSection: true,
};

export interface DamageCostEvaluationDamageCorrectionsDisplayProps {
  damageCostEvaluation: DamageCostEvaluationDto;
  corrections: Nil<DamageCostEvaluationDamageCorrectionsDto>;
  displayProps?: typeof defaultDisplayProps;
}

export function DamageCostEvaluationDamageCorrectionsDisplay({
  damageCostEvaluation,
  corrections,
  displayProps,
}: DamageCostEvaluationDamageCorrectionsDisplayProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const tenantProfile = useTenantProfileV2();

  const isAnyCorrections =
    !!corrections && corrections.correctionStatus === DamageCorrectionStatus.Corrected;
  const entityOwnerTenantId = damageCostEvaluation.tenantId;
  const vehicleOwnerTenantId = damageCostEvaluation.vehicle?.tenantId;
  const isEntityOwnerTenant = tenantProfile.profile?.id === entityOwnerTenantId;
  const isVehicleOwnerTenant = tenantProfile.profile?.id === vehicleOwnerTenantId;

  return (
    <Box>
      {!isAnyCorrections && <NoDataAlert title='No damage corrections' description={undefined} />}

      {isAnyCorrections && (
        <Stack gap={2}>
          {displayProps?.alertsSection && (
            <>
              {!isEntityOwnerTenant && (
                <Alert severity='warning'>
                  The corrections can be applied only by the operation owner company{" "}
                  <TenantLink entityId={entityOwnerTenantId} entity={undefined} />.
                </Alert>
              )}

              {!isVehicleOwnerTenant && (
                <Alert severity='warning'>
                  The corrections can be applied only by the vehicle owner company{" "}
                  <TenantLink entityId={vehicleOwnerTenantId} entity={undefined} />.
                </Alert>
              )}

              {!corrections.canApply && corrections?.applyStatus !== ApplyStatus.Applied && (
                <Alert severity='info'>
                  You can apply corrections only on{" "}
                  <strong>
                    <InlineApiEnumValue
                      type={ApiEnumName.DamageCostEvaluationStage}
                      value={DamageCostEvaluationStage.Completed}
                    />
                  </strong>{" "}
                  stage.
                </Alert>
              )}

              {corrections?.applyStatus === ApplyStatus.Applied && (
                <Alert severity='success'>The corrections have been applied.</Alert>
              )}
            </>
          )}

          {displayProps?.overviewSection && (
            <Stack gap={1}>
              <AppTypography variant='h6'>Overview</AppTypography>

              <DamageCostEvaluationDamageCorrectionsOverviewDisplay corrections={corrections} />
            </Stack>
          )}

          {displayProps?.damagesOnVisualModelsSection && (
            <Stack gap={1}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xxs: "1fr",
                    md: "1fr 1fr",
                  },
                  columnGap: 2,
                  rowGap: 1,
                }}
              >
                <Stack spacing={1}>
                  <AppTypography
                    sx={{ width: "fit-content" }}
                    variant='h6'
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title:
                        "Original damages from the vehicle actual state before the correction.",
                    }}
                  >
                    Original damages
                  </AppTypography>

                  <Box>
                    {TypeHelper.isEmptyArray(
                      damageCostEvaluation.corrections?.initialAndOriginalVehicleDamageIds,
                    ) && <NoDataAlert title='No damages specified' />}

                    {!TypeHelper.isEmptyArray(
                      damageCostEvaluation.corrections?.initialAndOriginalVehicleDamageIds,
                    ) && (
                      <VisualModelViewerOfDamageCostEvaluation
                        entity={damageCostEvaluation}
                        damagesToDisplay='initialAndCorrectionsOriginal'
                        editorProps={{
                          visualModelViewerProps: {
                            variant: "compact",
                            direction: "column",
                            mainViewerProps: {
                              svgViewerProps: {
                                svgProps: {
                                  height: { xxs: 200, sm: 300, md: 400, lg: 500 },
                                  width: { xxs: 200, sm: 300, md: 400, lg: 500 },
                                  minWidth: { xxs: 100, sm: 300, md: 300, lg: 500 },
                                  minHeight: { xxs: 100, sm: 300, md: 300, lg: 500 },
                                  maxWidth: { xxs: 200, sm: 300, md: 500, lg: 1000 },
                                  maxHeight: { xxs: 200, sm: 300, md: 500, lg: 700 },
                                },
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </Stack>

                <Stack spacing={1}>
                  <AppTypography
                    sx={{ width: "fit-content" }}
                    variant='h6'
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title:
                        "Result damages created from the original corrected damages after the correction.",
                    }}
                  >
                    New damages
                  </AppTypography>

                  <Box>
                    <VisualModelViewerOfDamageCostEvaluation
                      entity={damageCostEvaluation}
                      damagesToDisplay='currentCorrected'
                      editorProps={{
                        visualModelViewerProps: {
                          variant: "compact",
                          direction: "column",
                          mainViewerProps: {
                            svgViewerProps: {
                              svgProps: {
                                height: { xxs: 200, sm: 300, md: 400, lg: 500 },
                                width: { xxs: 200, sm: 300, md: 400, lg: 500 },
                                minWidth: { xxs: 100, sm: 300, md: 300, lg: 500 },
                                minHeight: { xxs: 100, sm: 300, md: 300, lg: 500 },
                                maxWidth: { xxs: 200, sm: 300, md: 500, lg: 1000 },
                                maxHeight: { xxs: 200, sm: 300, md: 500, lg: 700 },
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}

          {displayProps?.appliedSection && (
            <Stack gap={1}>
              <AppTypography variant='h6'>Applied</AppTypography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xxs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gridTemplateRows: "auto",
                  rowGap: 1,
                  columnGap: 1,
                }}
              >
                <InfoTile
                  label='Applied status'
                  iconOf='status'
                  isEmpty={isNil(corrections?.applyStatus)}
                >
                  <InlineApiEnumValue
                    type={ApiEnumName.ApplyStatus}
                    value={corrections?.applyStatus}
                    withHelperTooltip
                  />
                </InfoTile>

                {corrections?.applyStatus === ApplyStatus.Applied && (
                  <>
                    <InfoTile
                      label='Applied at'
                      iconOf='date'
                      isEmpty={isNil(corrections?.appliedAt)}
                    >
                      <Datetime datetime={corrections?.appliedAt} />
                    </InfoTile>

                    <InfoTile
                      label='Applied by'
                      iconOf='user'
                      isEmpty={isNil(corrections?.appliedBy)}
                    >
                      <InlineUser userId={corrections?.appliedBy} withAvatar />
                    </InfoTile>
                  </>
                )}
              </Box>

              {corrections?.applyStatus === ApplyStatus.Applied && (
                <Stack direction='column' spacing={1}>
                  <FieldValue
                    label='Deactivated damages (original)'
                    isEmpty={TypeHelper.isEmptyArray(corrections?.vehicleDamageIdsDeactivated)}
                  >
                    <Stack gap={0}>
                      {corrections?.vehicleDamageIdsDeactivated?.map((vehicleDamageId) => (
                        <VehicleDamageLink
                          key={vehicleDamageId}
                          vehicleId={damageCostEvaluation.vehicle?.id}
                          entityId={vehicleDamageId}
                          entity={undefined}
                        />
                      ))}
                    </Stack>
                  </FieldValue>

                  <FieldValue
                    label='Created damages (new)'
                    isEmpty={TypeHelper.isEmptyArray(corrections?.vehicleDamageIdsCreated)}
                  >
                    <Stack gap={0}>
                      {corrections?.vehicleDamageIdsCreated?.map((vehicleDamageId) => (
                        <VehicleDamageLink
                          key={vehicleDamageId}
                          vehicleId={damageCostEvaluation.vehicle?.id}
                          entityId={vehicleDamageId}
                          entity={undefined}
                        />
                      ))}
                    </Stack>
                  </FieldValue>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}
